import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { DataTable } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { BudgetSettingsDialog } from "@/components/acquisitions/BudgetSettingsDialog";
import { BudgetSummaryStrip } from "@/components/acquisitions/BudgetSummaryStrip";
import { BudgetTransferDialog } from "@/components/acquisitions/BudgetTransferDialog";
import { BudgetFundsToolbar } from "@/components/acquisitions/BudgetFundsToolbar";
import { createBudgetFundColumns } from "@/components/acquisitions/budgetFundColumns";
import { useBudgetOverview } from "@/hooks/useBudgetOverview";
import { useAuth } from "@/contexts/AuthContext";
import { hasAnyPermission } from "@/lib/permissions";
import { Wallet, Plus, ArrowRightLeft, AlertTriangle, Settings2 } from "lucide-react";

const formatCurrency = (n: number) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(n);

export default function BudgetOverviewPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManageBudget = Boolean(
    user && hasAnyPermission(user.permissions, ["acquisitions.manageBudget"])
  );
  const budget = useBudgetOverview();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const columns = useMemo(() => createBudgetFundColumns(navigate, formatCurrency), [navigate]);
  const warnings = budget.fundWarnings;

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Budget"
        description="Track fund allocation, availability, and spending."
        breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "Budget" }]}
        titleSize="md"
        showSearch={false}
        actions={
          <div className="flex flex-wrap gap-2">
            {canManageBudget && (
              <Button variant="outline" onClick={() => setSettingsOpen(true)}>
                <Settings2 className="mr-2 h-4 w-4" />
                Settings
              </Button>
            )}
            <Button variant="outline" onClick={() => budget.setTransferOpen(true)}>
              <ArrowRightLeft className="mr-2 h-4 w-4" />
              Transfer
            </Button>
            <Button onClick={() => navigate("/acquisitions/budget/funds/new")}>
              <Plus className="mr-2 h-4 w-4" />
              Add Fund
            </Button>
          </div>
        }
      />

      {warnings && warnings.threshold != null && warnings.funds.length > 0 && (
        <Alert variant="warning" className="items-start">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <AlertDescription>
            <span className="font-medium">
              {warnings.funds.length} fund(s) at or above {warnings.threshold}% utilization
            </span>
            <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-sm">
              {warnings.funds.map((f) => (
                <li key={f.id}>
                  {f.name} ({f.code}) — {f.utilizationPercent}% used
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {budget.summary && (
        <BudgetSummaryStrip summary={budget.summary} formatCurrency={formatCurrency} />
      )}

      <BudgetFundsToolbar
        search={budget.search}
        onSearchChange={budget.setSearch}
        fiscalYear={budget.fiscalYear}
        onFiscalYearChange={budget.setFiscalYear}
        yearOptions={budget.yearOptions}
        activeFilter={budget.activeFilter}
        onActiveFilterChange={budget.setActiveFilter}
        total={budget.total}
      />

      <DataTable
        columns={columns}
        data={budget.funds}
        rowKey={(row) => row.id}
        loading={budget.loading}
        emptyState={
          <EmptyState
            icon={<Wallet className="h-6 w-6 text-slate-400" />}
            title="No funds yet"
            description="Create a fund to start tracking acquisition budgets."
            action={
              <Button onClick={() => navigate("/acquisitions/budget/funds/new")}>
                <Plus className="mr-2 h-4 w-4" />
                Add Fund
              </Button>
            }
          />
        }
      />

      {canManageBudget && (
        <BudgetSettingsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          thresholdInput={budget.thresholdInput}
          onThresholdChange={budget.setThresholdInput}
          savingThreshold={budget.savingThreshold}
          onSaveThreshold={budget.saveThreshold}
          onClearThreshold={budget.clearThreshold}
          autoApproveInput={budget.autoApproveInput}
          onAutoApproveChange={budget.setAutoApproveInput}
          savingAutoApprove={budget.savingAutoApprove}
          onSaveAutoApprove={budget.saveAutoApprove}
          onClearAutoApprove={budget.clearAutoApprove}
        />
      )}

      <BudgetTransferDialog
        open={budget.transferOpen}
        onOpenChange={budget.setTransferOpen}
        funds={budget.funds}
        transferData={budget.transferData}
        onTransferDataChange={budget.setTransferData}
        transferring={budget.transferring}
        onTransfer={budget.handleTransfer}
        formatCurrency={formatCurrency}
      />
    </PageContainer>
  );
}
